/**
 * Single source of truth for stripping null/undefined event-prop values, shared
 * by the Plausible, GTM, and PostHog event paths (lib/analytics.ts,
 * lib/gtm-events.ts, lib/posthog-events.ts). Keeping it in one place means a
 * change to the sanitization rule can't silently diverge one tool from the others
 * — the cross-tool parity this analytics layer exists to provide.
 */
import { scrubUrl } from "./analytics-url";

export const stripNullish = (
  props?: Record<string, string | number | boolean | null | undefined>,
): Record<string, string | number | boolean> =>
  Object.fromEntries(
    Object.entries(props ?? {}).filter(
      (entry): entry is [string, string | number | boolean] => entry[1] != null,
    ),
  );

/**
 * Scrub every URL-shaped prop VALUE, whatever its key is called.
 *
 * ## The hole this closes
 *
 * `lib/analytics-url.ts` scrubs the URLs each SDK names for itself — `u`/`r`
 * for Plausible, `$current_url` and friends for PostHog. Its own comment said
 * "Nothing else in the payload carries a URL — it is the event name, domain and
 * our own custom props", and that last clause was wrong: `TrackedSignupLink`
 * sends the destination as an `href` prop.
 *
 * On the invite page that destination is
 * `/auth/signup?returnTo=%2Fapi%2Fsignup-redirect%3Finvite_token%3D<TOKEN>` —
 * so an invite token, a bearer credential that grants access to an account,
 * left the browser to Plausible, PostHog and Google on an `Auth Started` event.
 * `/get-started?returnTo=<anything>` had the same shape with lower stakes.
 *
 * ## Why by value and not by key
 *
 * A list of URL-bearing key names is a list someone has to remember to extend,
 * and this whole class of bug is what happens when they don't — the scrub was
 * built for PostHog, then Plausible went unscrubbed for three weeks, and now
 * custom props have walked past both. Keying on the VALUE means a prop added
 * next year called `target` or `link` is covered the day it ships.
 *
 * `scrubUrl` keeps the origin and a scrubbed path and drops search and hash
 * outright, so what survives is the part worth measuring — which destination —
 * without the part that should never have travelled. It returns anything that
 * is not URL-shaped unchanged, so ordinary props are untouched.
 */
export const scrubUrlValues = (
  props: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> =>
  Object.fromEntries(
    Object.entries(props).map(([key, value]) => [
      key,
      typeof value === "string" ? scrubUrl(value) : value,
    ]),
  );
